import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { 
  Swords, X, Calendar, Clock, MapPin, MessageSquare, 
  Trophy, Loader2, Gamepad2
} from "lucide-react";

const GAME_TYPES = ["Spades", "Whist", "Backgammon", "Checkers", "Chess", "Dominoes"];

const ChallengePlayerModal = ({ isOpen, onClose, opponent, token }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    game_type: "Spades",
    proposed_date: "",
    proposed_time: "",
    location: "",
    message: "",
    stakes: ""
  });

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.proposed_date || !formData.proposed_time) {
      toast.error("Please select a date and time");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/challenges`,
        {
          opponent_id: opponent.id,
          ...formData
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Challenge sent to ${opponent.full_name}!`);
      onClose();
      setFormData({
        game_type: "Spades",
        proposed_date: "",
        proposed_time: "",
        location: "",
        message: "",
        stakes: ""
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send challenge");
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <Card className="w-full max-w-md bg-[#0A0A0A] border border-white/10">
        <CardHeader className="border-b border-white/10 flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#7a6200]/20 flex items-center justify-center">
              <Swords className="w-5 h-5 text-[#7a6200]" />
            </div>
            <div>
              <CardTitle className="font-bebas text-xl tracking-wider text-white">
                CHALLENGE PLAYER
              </CardTitle>
              <p className="text-[#52525B] text-xs font-ubuntu">
                Challenge {opponent?.full_name}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-[#A1A1AA] hover:text-white"
            data-testid="close-challenge-modal"
          >
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>

        <CardContent className="p-6">
          {/* Opponent Info */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-white/5 rounded-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#7a6200]/50">
              {opponent?.avatar ? (
                <img src={opponent.avatar} alt={opponent.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-[#7a6200]/20 flex items-center justify-center">
                  <span className="text-[#7a6200] font-bebas text-xl">
                    {opponent?.full_name?.charAt(0) || "?"}
                  </span>
                </div>
              )}
            </div>
            <div>
              <p className="text-white font-bebas text-lg tracking-wider">{opponent?.full_name}</p>
              <div className="flex items-center gap-2 text-[#52525B] text-xs font-ubuntu">
                <span className="capitalize">{opponent?.skill_level || "Unknown"}</span>
                {opponent?.rating > 0 && (
                  <>
                    <span>•</span>
                    <span>★ {opponent.rating}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Game Type */}
            <div>
              <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                <Gamepad2 className="w-4 h-4 inline mr-2" />
                Game Type *
              </label>
              <div className="grid grid-cols-3 gap-2">
                {GAME_TYPES.map((game) => (
                  <button
                    key={game}
                    type="button"
                    onClick={() => setFormData({ ...formData, game_type: game })}
                    className={`px-3 py-2 rounded text-sm font-ubuntu transition-colors ${
                      formData.game_type === game
                        ? "bg-[#7a6200] text-white"
                        : "bg-white/5 text-[#A1A1AA] hover:bg-white/10"
                    }`}
                    data-testid={`challenge-game-${game.toLowerCase()}`}
                  >
                    {game}
                  </button>
                ))}
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Date *
                </label>
                <Input
                  type="date"
                  value={formData.proposed_date}
                  onChange={(e) => setFormData({ ...formData, proposed_date: e.target.value })}
                  min={today}
                  className="input-dark"
                  required
                  data-testid="challenge-date"
                />
              </div>
              <div>
                <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                  <Clock className="w-4 h-4 inline mr-2" />
                  Time *
                </label>
                <Input
                  type="time"
                  value={formData.proposed_time}
                  onChange={(e) => setFormData({ ...formData, proposed_time: e.target.value })}
                  className="input-dark"
                  required
                  data-testid="challenge-time"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                <MapPin className="w-4 h-4 inline mr-2" />
                Location (Optional)
              </label>
              <Input
                type="text"
                placeholder="Where do you want to play?"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="input-dark"
                data-testid="challenge-location"
              />
            </div>

            {/* Stakes */}
            <div>
              <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                <Trophy className="w-4 h-4 inline mr-2" />
                Stakes (Optional)
              </label>
              <Input
                type="text"
                placeholder="e.g., Bragging rights, Lunch, $20"
                value={formData.stakes}
                onChange={(e) => setFormData({ ...formData, stakes: e.target.value })}
                className="input-dark"
                data-testid="challenge-stakes"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-ubuntu text-[#A1A1AA] mb-2">
                <MessageSquare className="w-4 h-4 inline mr-2" />
                Message (Optional)
              </label>
              <Textarea
                placeholder="Add a personal message to your challenge..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="input-dark min-h-[80px]"
                data-testid="challenge-message"
              />
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1 border-white/20 text-[#A1A1AA]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 btn-gold"
                data-testid="send-challenge-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Swords className="w-4 h-4 mr-2" />
                    Send Challenge
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChallengePlayerModal;
